/*
 * File: createPassword.slice
 * Keep track of the createPassword process.
 *
 * The slice is connected with the RTK Query (createPasswordApi) to unify shared statuses among the three main processes.
 * - start: RTK has its own api for the start process (loading, error, data ...)
 * - next: RTK has its own api for the next process (loading, error, data ...)
 * - back: RTK has its own api for the back process (loading, error, data ...)
 *
 * This slice derive the statuses from the 3 APIs above, merging the same statuses (eg. the 3 loading statuses) into a single one.
 */
import { isAnyOf, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { PageContent } from "../../types/components.type";
import { RedirectErrorData } from "../../types/error.type";
import {
  createGenericState,
  onFulfilledMatcher,
  onPendingMatcher,
  onRejectMatcher,
} from "../genericState/genericState";
import {
  allCreatePasswordFulfilledMatchers,
  allCreatePasswordPendingMatchers,
  allCreatePasswordRejectedMatchers,
  createPasswordApi,
} from "../../api/createPasswordApi";

export type CreatePasswordState = {
  isLoading: boolean;
  processId?: string;
  isError: boolean;
  isSuccess: boolean;
  isUninitialized: boolean;
  error: SerializedError | FetchBaseQueryError | undefined;
  errorCode?: number;
  data?: PageContent;
  currentStep: number;
  lastAction?: string;
  stepValues?: Record<string, unknown>;
  redirectError?: RedirectErrorData;
};

const initialState: CreatePasswordState = {
  isLoading: false,
  isError: false,
  isSuccess: false,
  isUninitialized: true,
  error: undefined,
  data: undefined,
  currentStep: 0,
  redirectError: undefined,
};

export const createPasswordSlice = createGenericState(
  "createPassword",
  initialState,
  {
    reducers: {
      reset() {
        return initialState;
      },
      setLoading(state, action: PayloadAction<boolean>) {
        state.isLoading = action.payload;
      },
    },
    extraReducers(builder) {
      builder
        .addMatcher(
          createPasswordApi.endpoints.nextPasswordCreate.matchPending,
          (state, action) => {
            state.stepValues = action.meta.arg.originalArgs.values;
          },
        )
        .addMatcher(
          isAnyOf(...allCreatePasswordPendingMatchers),
          (state, action) => {
            return onPendingMatcher(state, action);
          },
        )
        .addMatcher(
          isAnyOf(...allCreatePasswordRejectedMatchers),
          (state, action) => {
            onRejectMatcher(state, action);
          },
        )
        .addMatcher(
          isAnyOf(...allCreatePasswordFulfilledMatchers),
          (state, action) => {
            onFulfilledMatcher(state, action);
          },
        );
    },
  },
);

export const createPasswordActions = {
  ...createPasswordSlice.actions,
};
export default createPasswordSlice;
